import { useCallback, useEffect, useState } from "react";
import {
  isBrowserLoadableAssetUrl,
  normalizeAssetUrlForStorage,
  resolveApiAssetUrl,
  resolveDisplayAssetUrl,
} from "../utils/api";

const ProfilePlaceholderIcon = () => (
  <svg className="w-12 h-12 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

export function getValidProfilePicUrl(profilePic) {
  if (!profilePic || typeof profilePic !== "string") return "";
  const trimmed = profilePic.trim();
  if (!trimmed) return "";
  return resolveApiAssetUrl(trimmed);
}

const TutorProfileAvatar = ({
  profilePic,
  name = "Tutor",
  className = "w-24 h-24",
  showInitialFallback = false,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(Boolean(profilePic));
  const [retriedSignedUrl, setRetriedSignedUrl] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!profilePic) {
        setSrc("");
        setImageFailed(false);
        setRetriedSignedUrl(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setImageFailed(false);
      setRetriedSignedUrl(false);

      const stored = normalizeAssetUrlForStorage(profilePic) || profilePic;
      const displayUrl = await resolveDisplayAssetUrl(stored);
      if (cancelled) return;

      if (displayUrl) {
        setSrc(displayUrl);
      } else if (isBrowserLoadableAssetUrl(profilePic)) {
        setSrc(getValidProfilePicUrl(profilePic));
      } else {
        setSrc("");
        setImageFailed(true);
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [profilePic]);

  const handleImageError = useCallback(async () => {
    const stored = normalizeAssetUrlForStorage(profilePic) || profilePic;
    if (retriedSignedUrl || !stored) {
      setImageFailed(true);
      return;
    }
    try {
      const signedUrl = await resolveDisplayAssetUrl(stored);
      if (signedUrl) {
        setRetriedSignedUrl(true);
        setSrc(signedUrl);
        setImageFailed(false);
        return;
      }
    } catch {
      // fall through to placeholder
    }
    setImageFailed(true);
  }, [profilePic, retriedSignedUrl]);

  const showPhoto = Boolean(src) && !imageFailed && !loading;
  const initial = String(name || "Tutor").trim().charAt(0).toUpperCase() || "T";

  if (showPhoto) {
    return (
      <img
        src={src}
        alt={name}
        className={`${className} shrink-0 rounded-full object-cover`}
        onError={handleImageError}
      />
    );
  }

  return (
    <div
      className={`${className} shrink-0 rounded-full flex items-center justify-center ${
        showInitialFallback
          ? "bg-brand-medium-blue text-lg font-bold text-white"
          : "bg-gray-300"
      }`}
      aria-label={name}
    >
      {showInitialFallback ? initial : <ProfilePlaceholderIcon />}
    </div>
  );
};

export default TutorProfileAvatar;
